import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, AlertCircle, CheckCircle, User } from 'lucide-react';
import { validateInternEmail, isVipInternEmail } from '../data/tasks';

const AuthModal = ({ isOpen, onClose, trackName, onAuthenticated }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    await new Promise(resolve => setTimeout(resolve, 800));

    // Import directly to check VIP
    const isValid = validateInternEmail(email, trackName);
    
    // We only allow VIPs to bypass the closed internship
    if (isValid && isVipInternEmail(email)) {
      setSuccess(true);
      setTimeout(() => {
        onAuthenticated(email);
        handleClose();
      }, 1200);
    } else {
      setError("The internship program (Batch IV) is now closed.");
    }

    setIsLoading(false);
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setSuccess(false);
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-primary/40 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-primary px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-center justify-between gap-2 sm:gap-3">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white flex-shrink-0">
                  <Lock className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-xl font-bold text-white truncate">Access Required</h2>
                  <p className="text-white/80 text-xs sm:text-sm truncate">Verify your enrollment</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 transition-colors text-white flex-shrink-0"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            <div className="text-center mb-5 sm:mb-6">
              <h3 className="text-sm sm:text-base font-semibold text-neutral-900 mb-1 sm:mb-2">
                {trackName} Internship
              </h3>
              <p className="text-neutral-500 text-xs sm:text-sm">
                Enter your registered email to access your learning track.
              </p>
            </div>

            {/* Success State */}
            {success && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-5 sm:py-6"
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-secondary/15 rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-lg shadow-secondary/20">
                  <CheckCircle className="w-7 h-7 sm:w-8 sm:h-8 text-secondary" />
                </div>
                <h4 className="text-secondary font-bold text-base sm:text-lg mb-1">Access Granted</h4>
                <p className="text-neutral-500 text-xs sm:text-sm">Redirecting to your track...</p>
              </motion.div>
            )}

            {/* Form */}
            {!success && (
              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-neutral-700 mb-1.5 sm:mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                      <Mail className="w-4 h-4 text-neutral-400" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your registered email"
                      className="w-full pl-10 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 border border-neutral-300 rounded-xl text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-2 p-2 sm:p-3 bg-primary/5 border border-primary/20 rounded-xl text-primary text-xs sm:text-sm"
                  >
                    <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </motion.div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading || !email.trim()}
                  className="w-full bg-primary hover:bg-primary/90 text-white py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
                >
                  {isLoading ? (
                    <>
                      <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <User className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Access Track
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Help Text */}
            {!success && (
              <div className="mt-4 sm:mt-5 p-2.5 sm:p-4 bg-secondary/10 border border-secondary/20 rounded-xl">
                <p className="text-xs text-secondary-dark text-center font-medium">
                  <span className="font-bold">Need help?</span> Contact your mentor if you don't have access.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;
